import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { Save, Loader2 } from "lucide-react";
import api from "../../apiWithAccessToken";

const MONTHS = [
  { value: 1, label: "January" }, { value: 2, label: "February" },
  { value: 3, label: "March" }, { value: 4, label: "April" },
  { value: 5, label: "May" }, { value: 6, label: "June" },
  { value: 7, label: "July" }, { value: 8, label: "August" },
  { value: 9, label: "September" }, { value: 10, label: "October" },
  { value: 11, label: "November" }, { value: 12, label: "December" }
];

const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AUD", "CAD", "SGD"];

const calculateFyRangeNoYear = (startMonth, startDay) => {
  try {
    const month = parseInt(startMonth, 10);
    const day = parseInt(startDay, 10);
    if (!month || !day) return "";

    let endMonth;
    let endDay;

    if (day === 1) {
      endMonth = month === 1 ? 12 : month - 1;
      endDay = new Date(2020, endMonth, 0).getDate();
    } else {
      endMonth = month;
      endDay = day - 1;
    }

    return `${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")} to ${String(endMonth).padStart(2, "0")}-${String(endDay).padStart(2, "0")}`;
  } catch (e) {
    return "";
  }
};

export default function AppSettingsTab() {
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [timezones, setTimezones] = useState([]);

  const [formData, setFormData] = useState({
    chat_trim_count: "50",
    currency: "INR",
    timezone: "Asia/Kolkata",
    fy_month: 4,
    fy_start_day: 1
  });

  // Dynamically calculate days based on the CURRENT year (e.g., 2026)
  const currentYear = new Date().getFullYear();

  const daysInSelectedMonth = useMemo(() => {
    // This will correctly return 28 for Feb in 2026
    const days = new Date(currentYear, formData.fy_month, 0).getDate();
    return Array.from({ length: days }, (_, i) => i + 1);
  }, [formData.fy_month, currentYear]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [tzResult, settingsResult] = await Promise.allSettled([
        api.get("/timezones"),
        api.get("/app-settings")
      ]);

      if (tzResult.status === "fulfilled") {
        setTimezones(Array.isArray(tzResult.value.data) ? tzResult.value.data : []);
      }

      if (settingsResult.status === "fulfilled" && settingsResult.value.data) {
        const settingsData = settingsResult.value.data;
        
        let fy_month = 4;
        let fy_start_day = 1;
        
        if (settingsData.financial_year) {
          const startDateStr = settingsData.financial_year.split(" to ")[0];
          if (startDateStr && startDateStr.includes("-")) {
            const parts = startDateStr.split("-");
            if (parts.length === 2) {
               fy_month = parseInt(parts[0], 10);
               fy_start_day = parseInt(parts[1], 10);
            }
          }
        }

        setFormData({
          chat_trim_count: settingsData.chat_trim_count || "50",
          currency: settingsData.currency || "INR",
          timezone: settingsData.timezone || "Asia/Kolkata",
          fy_month: fy_month,
          fy_start_day: fy_start_day
        });
      }

      const networkFailure = [tzResult, settingsResult].some(
        (result) => result.status === "rejected" && !result.reason?.response
      );
      if (networkFailure) {
        throw new Error("Network failure");
      }
    } catch (error) {
      toast.error("Failed to load application settings.");
    } finally {
      setLoading(false);
    }
  };

  const updateSingleSetting = async (id, payload) => {
    try {
      const res = await api.put(`/app-settings/${id}`, payload);
      const contentType = res.headers?.["content-type"] || "";
      if (!contentType.includes("application/json")) {
        throw new Error(`Server returned non-JSON response for ID ${id}`);
      }

      return res.data;
    } catch (error) {
      if (error?.response) {
        throw new Error(error.response.data?.message || `Failed to update ID ${id}`);
      }
      throw error;
    }
  };

  const handleSaveAll = async () => {
    if (parseInt(formData.chat_trim_count) > 90 || parseInt(formData.chat_trim_count) < 1) {
        toast.error("Chat History Duration must be between 1 and 90 days.");
        return;
    }
    setIsSaving(true);
    try {
      await Promise.all([
        updateSingleSetting(1, { key_value: formData.chat_trim_count }),
        updateSingleSetting(2, { start_month: formData.fy_month, start_day: formData.fy_start_day }),
        updateSingleSetting(3, { key_value: formData.currency }),
        updateSingleSetting(4, { key_value: formData.timezone })
      ]);

      toast.success("All settings updated successfully.");
    } catch (error) {
      console.error("Bulk save error:", error);
      toast.error(error.message || "An error occurred while saving.");
    } finally {
      setIsSaving(false);
    }
  };

  const fyDisplayString = useMemo(
    () => calculateFyRangeNoYear(formData.fy_month, formData.fy_start_day),
    [formData.fy_month, formData.fy_start_day]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[300px]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  const formatDisplayString = (rawStr) => {
    if(!rawStr) return "";
    try {
      const parts = rawStr.split(" to ");
      const formatPart = (p) => {
        const [m, d] = p.split("-");
        const monthName = MONTHS.find(x => x.value === parseInt(m))?.label.substring(0, 3) || m;
        return `${monthName} ${d}`;
      };
      return `${formatPart(parts[0])} to ${formatPart(parts[1])}`;
    } catch (e) {
      return rawStr;
    }
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div
        className="border rounded-xl shadow-sm flex flex-col"
        style={{ borderColor: 'var(--border)', backgroundColor: 'var(--surface)' }}
      >
        <div
          className="px-6 py-4 border-b flex items-center justify-between"
          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background)' }}
        >
          <div>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>Application Settings</h2>
            <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>Manage how your application works, including chat limits, currency, and time settings.</p>
          </div>
          <button
            onClick={handleSaveAll}
            disabled={isSaving || formData.chat_trim_count > 90 || formData.chat_trim_count < 1}
            className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
          
          <div>
            <label className="block text-sm font-medium" style={{ color: 'var(--foreground)' }}>Chat History Duration</label>
            <p className="text-xs mt-1 mb-2 min-h-[48px]" style={{ color: 'var(--muted-foreground)' }}>Number of days to keep chat history before it is automatically cleared (Max: 90 days).</p>
            <input
              type="number"
              min="1"
              max="90"
              placeholder="Enter number of days (e.g., 30, 90 ...)"
              className={`block w-full rounded-md shadow-sm sm:text-sm px-3 py-2 border text-[var(--foreground)] ${
                formData.chat_trim_count > 90 || formData.chat_trim_count < 1
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500" 
                  : "focus:border-indigo-500 focus:ring-indigo-500"
              }`}
              style={{
                backgroundColor: 'var(--surface)',
                borderColor: (formData.chat_trim_count > 90 || formData.chat_trim_count < 1) ? undefined : 'var(--border)'
              }}
              value={formData.chat_trim_count}
              onChange={(e) => {
                // Prevent typing more than 2 digits physically
                const val = e.target.value.slice(0, 2);
                setFormData({ ...formData, chat_trim_count: val });
              }}
            />
            {(formData.chat_trim_count > 90 || formData.chat_trim_count < 1) && (
              <p className="text-xs text-red-600 mt-1.5 font-medium">
                You cannot enter a number more than 90 or less than 1.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium" style={{ color: 'var(--foreground)' }}>Default Currency</label>
            <p className="text-xs mt-1 mb-2 min-h-[48px]" style={{ color: 'var(--muted-foreground)' }}>Choose the currency used for financial values.</p>
            <select
              className="block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border text-[var(--foreground)]"
              style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border)' }}
              value={formData.currency}
              onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
            >
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <div className="flex justify-between items-end mb-2">
               <div>
                  <label className="block text-sm font-medium" style={{ color: 'var(--foreground)' }}>Financial Year Start Date</label>
                  <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>Select when your financial year begins.</p>
               </div>
               {fyDisplayString && (
                  <span className="text-[10px] font-medium text-indigo-600 bg-indigo-50 px-2 py-1 rounded border border-indigo-100">
                    Range: {formatDisplayString(fyDisplayString)}
                  </span>
               )}
            </div>
            <div className="flex items-start gap-3 w-full">
              <div className="w-1/2">
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--muted-foreground)' }}>Month</label>
                <select
                  className="block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border text-[var(--foreground)]"
                  style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border)' }}
                  value={formData.fy_month}
                  onChange={(e) => {
                    const newMonth = parseInt(e.target.value);
                    const maxDaysInNewMonth = new Date(currentYear, newMonth, 0).getDate();
                    setFormData(prev => ({ 
                      ...prev, 
                      fy_month: newMonth,
                      fy_start_day: prev.fy_start_day > maxDaysInNewMonth ? maxDaysInNewMonth : prev.fy_start_day
                    }));
                  }}
                >
                  {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div className="w-1/2">
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--muted-foreground)' }}>Date</label>
                <select
                  className="block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border text-[var(--foreground)]"
                  style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border)' }}
                  value={formData.fy_start_day}
                  onChange={(e) => setFormData({ ...formData, fy_start_day: parseInt(e.target.value) })}
                >
                  {daysInSelectedMonth.map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium" style={{ color: 'var(--foreground)' }}>System Timezone</label>
            <p className="text-xs mt-1 mb-2" style={{ color: 'var(--muted-foreground)' }}>Select your time zone for logs, and queries.</p>
            <div className="h-[22px]" aria-hidden="true"></div>
            <select
              className="block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border text-[var(--foreground)]"
              style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border)' }}
              value={formData.timezone}
              onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
            >
              {timezones.length > 0 ? (
                timezones.map(tz => <option key={tz} value={tz}>{tz}</option>)
              ) : (
                <option value={formData.timezone}>{formData.timezone}</option>
              )}
            </select>
          </div>

        </div>
      </div>
    </div>
  );
}
